import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Users, CheckSquare, Square, Zap, ExternalLink } from "lucide-react";
import { REMINDER_TEMPLATES } from "@/components/admin/ReminderDialog";

function fillPreview(text, s) {
  const dues = Math.max(
    0,
    Number(s?.total_fees || 0) -
      Number(s?.fees_paid || 0) -
      Number(s?.discount_amount || 0),
  );
  return (text || "")
    .replaceAll("{name}", s?.full_name || "resident")
    .replaceAll("{dues}", dues.toLocaleString("en-IN"))
    .replaceAll("{room}", s?.flat_number || "-")
    .replaceAll("{total}", Number(s?.total_fees || 0).toLocaleString("en-IN"))
    .replaceAll("{paid}", Number(s?.fees_paid || 0).toLocaleString("en-IN"));
}

export default function BulkRemindersTab() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState({});
  const [templateIdx, setTemplateIdx] = useState(0);
  const [message, setMessage] = useState(REMINDER_TEMPLATES[0].text);
  const [onlyDues, setOnlyDues] = useState(true);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [counts, setCounts] = useState({});

  const load = async () => {
    const { data } = await api.get("/students");
    setStudents(data || []);
    try {
      const c = await api.get("/reminders/counts");
      setCounts(c.data || {});
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return students.filter((s) => (onlyDues ? Number(s.dues) > 0 : true));
  }, [students, onlyDues]);

  const allSelected = filtered.length > 0 && filtered.every((s) => selected[s.id]);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const totalOutstanding = filtered
    .filter((s) => selected[s.id])
    .reduce((sum, s) => sum + Math.max(0, Number(s.dues) || 0), 0);

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      setSelected(Object.fromEntries(filtered.map((s) => [s.id, true])));
    }
  };

  const pickTemplate = (i) => {
    setTemplateIdx(i);
    setMessage(REMINDER_TEMPLATES[i].text);
    setResults(null);
  };

  const send = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one student");
      return;
    }
    if (!message.trim()) {
      toast.error("Message can't be empty");
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const { data } = await api.post("/reminders/bulk", {
        student_ids: selectedIds,
        message,
        channel: "whatsapp",
      });
      setResults(data.results || []);
      toast.success(`${data.sent} reminders logged. Open WhatsApp links below to send.`);
      // Refresh counts
      const c = await api.get("/reminders/counts");
      setCounts(c.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk send failed");
    } finally {
      setSending(false);
    }
  };

  const openAll = () => {
    if (!results) return;
    // Collect all links (student + parent per row)
    const links = [];
    results.forEach((r) => {
      if (!r.ok) return;
      (r.wa_links || []).forEach((l) => links.push(l.link));
    });
    if (links.length === 0) {
      toast.warning("No WhatsApp links available");
      return;
    }
    const first = links.slice(0, 3);
    first.forEach((link, idx) => setTimeout(() => window.open(link, "_blank", "noopener,noreferrer"), idx * 150));
    if (links.length > first.length) {
      toast.info(
        `Opened ${first.length} of ${links.length} — browsers block bulk popups. Click each chat below.`,
      );
    }
  };

  return (
    <div data-testid="bulk-reminders-tab">
      <div className="mb-6">
        <h3 className="font-heading font-black text-3xl tracking-tighter">
          Bulk WhatsApp Reminders
        </h3>
        <p className="text-sm font-medium text-[#4A4A4A] mt-1">
          Send fee-reminder WhatsApps to many students in one go. Every send is logged in each student's history — badges update instantly.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left — recipient list */}
        <div className="lg:col-span-3 brutal-card p-0 overflow-hidden bg-white">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A0A0A] text-white">
            <div className="flex items-center gap-2 font-heading font-black tracking-tight">
              <Users size={16} strokeWidth={2.5} />
              Recipients ({selectedIds.length}/{filtered.length})
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer">
              <input
                type="checkbox"
                checked={onlyDues}
                onChange={(e) => setOnlyDues(e.target.checked)}
                className="accent-[#FFE600]"
                data-testid="bulk-onlydues-toggle"
              />
              Only students with dues
            </label>
          </div>

          <div className="max-h-[65vh] overflow-y-auto">
            <table className="w-full text-left text-sm" data-testid="bulk-students-table">
              <thead className="bg-[#F8F9FA] sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <button
                      onClick={toggleAll}
                      data-testid="bulk-select-all"
                      className="p-1 border-2 border-[#0A0A0A] rounded bg-white"
                      title={allSelected ? "Deselect all" : "Select all"}
                    >
                      {allSelected ? <CheckSquare size={14} strokeWidth={2.5} /> : <Square size={14} strokeWidth={2.5} />}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-xs uppercase tracking-widest">Name</th>
                  <th className="px-3 py-2 text-xs uppercase tracking-widest">Room</th>
                  <th className="px-3 py-2 text-xs uppercase tracking-widest">Dues</th>
                  <th className="px-3 py-2 text-xs uppercase tracking-widest">Sent</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-3 py-10 text-center font-medium text-[#4A4A4A]">
                      No students match this filter.
                    </td>
                  </tr>
                )}
                {filtered.map((s) => {
                  const dues = Math.max(0, Number(s.dues) || 0);
                  return (
                    <tr
                      key={s.id}
                      className="border-t-2 border-[#0A0A0A] hover:bg-[#FFFCE0]"
                      data-testid={`bulk-row-${s.id}`}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={!!selected[s.id]}
                          onChange={(e) => setSelected({ ...selected, [s.id]: e.target.checked })}
                          className="w-4 h-4 accent-[#00CC66]"
                          data-testid={`bulk-check-${s.id}`}
                        />
                      </td>
                      <td className="px-3 py-2.5 font-bold">
                        {s.full_name}
                        <div className="text-[10px] font-medium text-[#4A4A4A]">
                          {s.parent_contact_number || s.contact_number || "no contact"}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs font-bold">{s.flat_number || "-"}</td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-block px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full text-[11px] font-bold ${
                            dues <= 0 ? "bg-[#00CC66] text-white" : "bg-[#FF4D4D] text-white"
                          }`}
                        >
                          {dues <= 0 ? "PAID" : `₹${dues.toLocaleString("en-IN")}`}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs font-bold">
                        {counts[s.id]?.count || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selectedIds.length > 0 && (
            <div
              className="border-t-2 border-[#0A0A0A] px-4 py-3 flex items-center justify-between bg-[#FFE600]"
              data-testid="bulk-selection-summary"
            >
              <div className="font-heading font-black tracking-tight">
                {selectedIds.length} selected · Total outstanding ₹
                {totalOutstanding.toLocaleString("en-IN")}
              </div>
              <button
                onClick={() => setSelected({})}
                className="text-xs font-bold underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Right — compose */}
        <div className="lg:col-span-2 space-y-4">
          <div className="brutal-card p-5 bg-white">
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Template</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {REMINDER_TEMPLATES.map((t, i) => (
                <button
                  key={t.name}
                  onClick={() => pickTemplate(i)}
                  data-testid={`bulk-template-${i}`}
                  className={`px-3 py-1.5 text-xs font-bold border-2 border-[#0A0A0A] rounded-full transition-transform ${
                    templateIdx === i ? "bg-[#0A0A0A] text-[#FFE600]" : "bg-white"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>

            <div className="text-xs font-bold uppercase tracking-widest mb-1.5">Message</div>
            <textarea
              data-testid="bulk-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
            />
            <p className="text-[11px] font-medium text-[#4A4A4A] mt-1.5">
              Placeholders: {"{name} {dues} {room} {total} {paid}"} — replaced per student.
            </p>

            {selectedIds[0] && (
              <div className="mt-3 p-3 border-2 border-dashed border-[#0A0A0A] rounded-lg bg-[#FFFCE0] text-xs font-medium">
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1">
                  Preview for {students.find((x) => x.id === selectedIds[0])?.full_name}
                </div>
                {fillPreview(message, students.find((x) => x.id === selectedIds[0]))}
              </div>
            )}
          </div>

          <button
            onClick={send}
            disabled={sending || selectedIds.length === 0}
            data-testid="bulk-send-button"
            className="w-full inline-flex items-center justify-center gap-2 font-bold px-6 py-3.5 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-50"
          >
            <Send size={16} strokeWidth={2.5} />
            {sending ? "Logging..." : `Log ${selectedIds.length} reminders`}
          </button>

          {results && results.length > 0 && (
            <div
              className="brutal-card p-5 bg-white space-y-3"
              data-testid="bulk-results"
            >
              <div className="flex items-center justify-between">
                <div className="font-heading font-black tracking-tight">
                  {results.filter((r) => r.ok).length} logged
                </div>
                <button
                  onClick={openAll}
                  data-testid="bulk-open-all"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]"
                >
                  <ExternalLink size={12} strokeWidth={2.5} /> Open first 3
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {results.map((r) => (
                  <div
                    key={r.student_id}
                    className="flex items-center justify-between gap-2 text-xs font-medium border-b border-[#0A0A0A]/20 pb-2 last:border-b-0"
                    data-testid={`bulk-result-${r.student_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{r.student_name || "-"}</div>
                      <div className="text-[10px] text-[#4A4A4A] truncate">
                        {r.student_contact ? `Student: ${r.student_contact}` : ""}
                        {r.student_contact && r.parent_contact ? " · " : ""}
                        {r.parent_contact ? `Parent: ${r.parent_contact}` : ""}
                        {!r.student_contact && !r.parent_contact ? "no contact" : ""}
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {(r.wa_links || []).map((l) => (
                        <a
                          key={l.label}
                          href={l.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white whitespace-nowrap"
                          data-testid={`bulk-result-${r.student_id}-${l.label.toLowerCase()}`}
                        >
                          <MessageCircle size={11} strokeWidth={2.5} /> {l.label}
                        </a>
                      ))}
                      {(!r.wa_links || r.wa_links.length === 0) && (
                        <span className="text-[10px] font-bold text-[#FF4D4D]">
                          {r.reason || "no contact"}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#4A4A4A] font-medium">
                Note: browsers block bulk popups. Use the WhatsApp buttons above to send each — or click "Open first 3" to launch a burst.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
